import React from "react";
import Link from "next/link";
import { ArticleShort } from "lib/types/article";
import ProTag from "components/proTag/proTag";
import { BreadcrumbSeparator } from "components/icons/icons";

/**
 * Builds breadcrumb navigation items for an article
 * Format: [Pro Badge] → Sector → / → Category
 */
export const buildArticleBreadcrumbs = (
  article: ArticleShort,
  isLinkOrOptions:
    | boolean
    | {
        sectorIsLink?: boolean;
        categoryIsLink?: boolean;
      } = true
): React.ReactNode[] => {
  const items: React.ReactNode[] = [];
  const sectorIsLink =
    typeof isLinkOrOptions === "boolean"
      ? isLinkOrOptions
      : isLinkOrOptions.sectorIsLink ?? true;
  const categoryIsLink =
    typeof isLinkOrOptions === "boolean"
      ? isLinkOrOptions
      : isLinkOrOptions.categoryIsLink ?? true;

  // Add Pro badge if pro content
  if (article.isProContent) {
    items.push(<ProTag key="pro-badge" variant="pill" />);
  }

  // Add separator after Pro badge if there's sector or category following
  if (article.isProContent && (article.sector || article.category)) {
    items.push(
      <BreadcrumbSeparator
        key="separator-after-pro"
        className="h-4 w-[7px] shrink-0"
      />
    );
  }

  // Add sector if available (first link gets underline)
  if (article.sector) {
    if (sectorIsLink) {
      items.push(
        <Link
          key={`sector-${article.sector.slug}`}
          href={article.sector.url}
          className={`pointer-events-auto text-brand-neutral underline decoration-current underline-offset-2 transition-colors font-emphasis-14 hover:bg-mono-10 hover:underline ${article.category ? "shrink-0 whitespace-nowrap" : "min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap"}`}
          onClick={(e) => e.stopPropagation()}
        >
          {article.sector.name}
        </Link>
      );
    } else {
      items.push(
        <span
          key={`sector-${article.sector.slug}`}
          className={`text-brand-neutral font-emphasis-14 ${article.category ? "shrink-0 whitespace-nowrap" : "min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap"}`}
        >
          {article.sector.name}
        </span>
      );
    }
  }

  // Add separator before category if there are items before it
  if (article.category && items.length > 0) {
    items.push(
      <BreadcrumbSeparator key="separator" className="h-4 w-[7px] shrink-0" />
    );
  }

  // Add category if available (no underline if sector exists, underline only if it's the first element)
  if (article.category) {
    if (categoryIsLink) {
      items.push(
        <Link
          key={`category-${article.category.slug}`}
          href={article.category.url}
          className={`pointer-events-auto min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-brand-neutral transition-colors font-emphasis-14 hover:text-sifted ${!article.sector ? "decoration-current underline-offset-2 hover:underline" : "hover:underline"}`}
          onClick={(e) => e.stopPropagation()}
        >
          {article.category.name}
        </Link>
      );
    } else {
      items.push(
        <span
          key={`category-${article.category.slug}`}
          className="min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-brand-neutral font-emphasis-14"
        >
          {article.category.name}
        </span>
      );
    }
  }

  return items;
};

/**
 * Renders article breadcrumbs with proper spacing and wrapping
 *
 * Note: Parent is responsible for checking if breadcrumbs should be rendered
 * using `buildArticleBreadcrumbs(article).length > 0` before rendering this component.
 */
export const ArticleBreadcrumbs = ({ article }: { article: ArticleShort }) => {
  const breadcrumbItems = buildArticleBreadcrumbs(article);

  return (
    <div className="flex min-w-0 flex-nowrap items-center gap-1 overflow-hidden">
      {breadcrumbItems}
    </div>
  );
};
